import { StyleableButton } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Switch } from '@thanx/uikit/switch'
import {
  createFileUpload,
  getFileUpload,
  processFileUpload,
  SourcePath,
} from 'actions/fileUpload'
import axios from 'axios'
import ConfirmModal from 'components/ConfirmModal'
import FlagIcon from 'components/FlagIcon'
import InfoIcon from 'components/InfoIcon'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import { Fields as FileUpload, State } from 'models/FileUpload'
import { ValidationTypes } from 'models/Validation'
import React, { useRef, useState } from 'react'
import { useInterval } from 'web-api-hooks'
import CropperModal from './CropperModal'
import { checkIfImageIsSmall, getFileWithExactDimensions } from './helper'
import ImageButton from './ImageButton'

export type StandaloneImagePickerValue = {
  id: number | null
  record_id?: number | null
  url?: string
  uploading: boolean
  pendingImage?: string
}

type Props = {
  className?: string
  label?: string
  value?: StandaloneImagePickerValue
  onChange?: (value: StandaloneImagePickerValue) => void
  onFinalized?: (fileUploadId: number) => void
  removable?: boolean
  onRemoveComplete?: () => void
  imageWidth?: string
  thumbnailClassName?: string
  uploaderClassName?: string
  // Hardcoded validation options
  minWidth?: number
  minHeight?: number
  allowedExtensions?: string[]
  maxSizeMB?: number
  // Record type for file upload validation
  record_type?: ValidationTypes
}

const t = buildTranslate('cms.components.image_picker')
const DEFAULT_MAX_SIZE_MB = 4

// Standalone ImagePicker that works without Formsy
export const StandaloneImagePicker: React.FC<Props> = props => {
  const {
    value,
    onChange,
    label,
    className = '',
    imageWidth,
    thumbnailClassName,
    uploaderClassName,
    removable = false,
    onRemoveComplete,
    onFinalized,
    minWidth,
    minHeight,
    allowedExtensions = ['jpg', 'jpeg', 'png', 'gif'],
    maxSizeMB = DEFAULT_MAX_SIZE_MB,
    record_type,
  } = props

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isLowQualityModalOpen, setIsLowQualityModalOpen] = useState(false)
  const [imageToCrop, setImageToCrop] = useState<HTMLImageElement | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [image, setImage] = useState<HTMLImageElement | null>(null)
  const [isSmallImage, setIsSmallImage] = useState(false)
  const [fileUpload, setFileUpload] = useState<FileUpload | null>(null)
  const [uploadPercentage, setUploadPercentage] = useState<number | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const merchant = useCurrentMerchant()
  const dispatch = useDispatch()

  function handleError(message: string) {
    setError(message)
    // Don't update the value when there's an error - keep the previous state
    setFileUpload(null)
    setUploadPercentage(null)
    setIsProcessing(false)
  }

  // Poll the file upload to see if it is still processing.
  useInterval(
    async () => {
      if (fileUpload && merchant) {
        const response = await dispatch(
          getFileUpload(SourcePath.MERCHANTS, merchant.id, fileUpload.id)
        )
        if (response.type === 'fileUpload/GET_FAIL') {
          handleError(t('error'))
          return
        }

        const responseFileUpload = response.payload.data.file_upload
        if (responseFileUpload.state === State.FINALIZED) {
          setIsProcessing(false)
          setFileUpload(null)
          setUploadPercentage(null)

          if (onFinalized) {
            onFinalized(responseFileUpload.id)
          }

          const newValue = {
            id: responseFileUpload.id,
            record_id: responseFileUpload.record_id,
            url: '',
            uploading: false,
            pendingImage: image?.src,
          }
          onChange?.(newValue)
        } else if (responseFileUpload.state === State.ERRORED) {
          handleError(t('error'))
        }
      }
    },
    // Passing null clear the timer when there's nothing processing
    fileUpload ? 5000 : null
  )

  function reset() {
    const newValue = {
      id: null,
      record_id: null,
      url: '',
      uploading: false,
    }
    onChange?.(newValue)
    setError(null)
    setIsSmallImage(false)
    setImage(null)
    setIsLowQualityModalOpen(false)
    setImageToCrop(null)
    setSelectedFile(null)
    setUploadPercentage(null)
    setIsProcessing(false)
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>): void {
    if (event.target.files?.length !== 1) return
    reset()
    const file = event.target.files[0]
    setUploadPercentage(0)
    setSelectedFile(file)
    checkImageRestrictions(file)
  }

  async function checkImageRestrictions(file: File): Promise<void> {
    // Check file size
    const fileSizeInMB = file.size / (1024 * 1024)
    if (fileSizeInMB > maxSizeMB) {
      handleError(t('file_too_large', { size: fileSizeInMB.toFixed(1) }))
      return
    }

    // Check file extension
    const fileExtension = file.name.split('.').pop()?.toLowerCase()
    if (fileExtension && !allowedExtensions.includes(fileExtension)) {
      handleError(t('invalid_file_type'))
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      const img = new Image()
      img.onload = function () {
        if (
          !minWidth ||
          !minHeight ||
          img.width / img.height === minWidth / minHeight
        ) {
          if (
            checkIfImageIsSmall(img, minWidth ?? null, minHeight ?? null) &&
            file.type !== 'image/gif'
          ) {
            resizeImage(file)
            return
          }
          setSelectedFile(file)
          setImage(img)
          uploadImage(file)
          return
        }

        // if aspect ratio doesn't match open the cropper tool
        setImageToCrop(img)
      }
      img.onerror = () => {
        setUploadPercentage(null)
        setSelectedFile(null)
        setError(t('error'))
        return
      }
      // @ts-ignore
      img.src = reader.result
    }
    await reader.readAsDataURL(file)
  }

  async function uploadImage(file: File | null) {
    if (!merchant) return
    setIsLowQualityModalOpen(false)
    if (!file) {
      setUploadPercentage(null)
      return
    }

    // Only set uploading state if we're starting a new upload
    const newValue = {
      id: value?.id || null,
      record_id: value?.record_id || null,
      url: '',
      uploading: true,
    }
    onChange?.(newValue)

    // Create the file upload
    const fileExtension = file.type.split('/').pop() || ''

    const fileResponse = await dispatch(
      createFileUpload(SourcePath.MERCHANTS, merchant.id, {
        file_type: fileExtension,
        settings: {
          relation_name: 'image',
        },
        record_type: record_type,
      })
    )

    if (fileResponse.type === 'fileUpload/CREATE_FAIL') {
      // Revert the uploading state on error
      const errorValue = {
        id: value?.id || null,
        record_id: value?.record_id || null,
        url: value?.url,
        uploading: false,
      }
      onChange?.(errorValue)
      handleError(t('error'))
      return
    }
    const fileUpload = fileResponse.payload.data.file_upload

    // Upload to s3
    try {
      if (!fileUpload.upload_url) {
        // Revert the uploading state on error
        const errorValue = {
          id: value?.id || null,
          record_id: value?.record_id || null,
          url: value?.url,
          uploading: false,
        }
        onChange?.(errorValue)
        handleError(t('error'))
        return
      }
      setUploadPercentage(0)
      const uploadResponse = await axios.put(fileUpload.upload_url, file, {
        headers: { 'content-type': file.type },
        onUploadProgress: progressEvent => {
          const { loaded, total } = progressEvent
          const percent = Math.floor((loaded * 100) / total)
          setUploadPercentage(percent)
        },
      })

      if (!(uploadResponse.status >= 200 && uploadResponse.status < 300)) {
        // Revert the uploading state on error
        const errorValue = {
          id: value?.id || null,
          record_id: value?.record_id || null,
          url: '',
          uploading: false,
        }
        onChange?.(errorValue)
        handleError(t('error'))
        return
      }
      setIsProcessing(true)
    } catch (error) {
      // Revert the uploading state on error
      const errorValue = {
        id: value?.id || null,
        record_id: value?.record_id || null,
        url: value?.url,
        uploading: false,
      }
      onChange?.(errorValue)
      handleError(t('error'))
      return
    }

    // Process the file
    const processResponse = await dispatch(
      processFileUpload(
        SourcePath.MERCHANTS,
        fileUpload.source_id,
        fileUpload.id
      )
    )

    if (processResponse.type === 'fileUpload/PROCESS_FAIL') {
      // Revert the uploading state on error
      const errorValue = {
        id: value?.id || null,
        record_id: value?.record_id || null,
        url: value?.url,
        uploading: false,
      }
      onChange?.(errorValue)
      handleError(t('error'))
      return
    }
    setFileUpload(processResponse.payload.data.file_upload)
  }

  function resizeImage(file: File) {
    const reader = new FileReader()
    reader.onload = () => {
      const img = new Image()
      img.onload = async function () {
        const newFile = await getFileWithExactDimensions(
          img,
          minWidth ?? img.width,
          minHeight ?? img.height,
          file.name,
          file.type
        )
        setSelectedFile(newFile)
        setImage(img)
        if (checkIfImageIsSmall(img, minWidth ?? null, minHeight ?? null)) {
          setIsLowQualityModalOpen(true)
          setIsSmallImage(true)
          return
        }
        uploadImage(newFile)
      }
      img.onerror = () => {
        setUploadPercentage(null)
        setSelectedFile(null)
        setError(t('error'))
        return
      }
      // @ts-ignore
      img.src = reader.result
    }
    reader.readAsDataURL(file)
  }

  function removeImage() {
    reset()
    if (onRemoveComplete) onRemoveComplete()
  }

  const imageTypes: string[] = []
  allowedExtensions.map(extension => {
    if (['jpg', 'jpeg'].includes(extension)) {
      return imageTypes.push('image/jpeg')
    }
    return imageTypes.push(`image/${extension}`)
  })
  const acceptedImages = imageTypes.join(', ')

  return (
    <div className={className}>
      <Switch condition={!!label}>
        <label className="font-size-14 bold">{label}</label>
      </Switch>
      <ImageButton
        imageWidth={imageWidth}
        thumbnailClassName={thumbnailClassName}
        uploaderClassName={uploaderClassName}
        onClick={() => fileInputRef.current?.click()}
        src={image?.src || value?.pendingImage || value?.url}
        fileName={selectedFile?.name}
        uploadPercentage={uploadPercentage}
        processing={isProcessing}
      />
      <input
        data-testid="upload"
        className="hidden"
        accept={acceptedImages}
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
      />
      {!!minWidth && !!minHeight && !!imageToCrop && (
        <CropperModal
          isOpen
          onClose={() => reset()}
          onConfirm={file => {
            setImageToCrop(null)
            resizeImage(file)
          }}
          width={minWidth}
          height={minHeight}
          image={imageToCrop}
          imageName={selectedFile?.name}
          imageType={selectedFile?.type || imageTypes[0]}
          onSelectImage={() => fileInputRef.current?.click()}
        />
      )}
      <ConfirmModal
        title={t('low_quality_modal.title')}
        description={t('low_quality_modal.description')}
        confirmText={t('low_quality_modal.confirm')}
        isOpen={isLowQualityModalOpen}
        onClose={() => reset()}
        onConfirm={() => uploadImage(selectedFile)}
      />
      <Switch condition={!!error}>
        <Notification kind="negative" size="full">
          <InfoIcon color="#b64b26" />
          <span className="ml-xs body-text-4">{error}</span>
        </Notification>
      </Switch>
      <Switch condition={isSmallImage}>
        <Notification kind="warning" size="full">
          <FlagIcon color="#ad8813" />
          <span className="ml-xs body-text-4">{t('image_smaller')}</span>
        </Notification>
      </Switch>
      <Switch condition={!!value?.url && removable}>
        <div className="pb-s mt-m">
          <StyleableButton
            className="float-right font-size-12 belize-hole-50"
            onClick={() => removeImage()}
          >
            {t('delete')}
          </StyleableButton>
        </div>
      </Switch>
    </div>
  )
}

export default StandaloneImagePicker
